import { getProviderFromIntegrationClientKind } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useSelectClientsStepDataQuery } from '@/modules/household/LinkHouseholdDataTrowser/SelectClientsStep/graphql/SelectClientsStep.generated';
import { IntegrationClientKind } from '@/types/schema';

import { useManageIntegrationDataContext } from '../context/manageIntegrationData.context';

/**
 * Display name of the provider this household is linked to ("Addepar",
 * "eMoney"), for the copy and column headers throughout the flow. Falls back
 * to a neutral word before a link exists.
 *
 * Deliberately not `useAssetProviderDisplayName`, which answers a different
 * question: what the *tenant* can connect to. That one returns "Asset
 * providers" when more than one is enabled, which is wrong here — every string
 * in this flow names the one provider whose data is on screen.
 */
export function useIntegrationProviderName(): string {
  const { householdIntegrationLink } = useHouseholdDetailsContext();
  const { householdId } = useManageIntegrationDataContext();
  const { data } = useSelectClientsStepDataQuery({
    variables: { householdId },
    skip: !householdId,
    fetchPolicy: 'cache-and-network',
  });

  const household = data?.households?.edges?.[0]?.node;
  // The client typeahead enforces that every selected client uses one provider,
  // so any linked client's kind identifies the active integration.
  const selectedIntegrationKind = household?.integrationClients?.find(
    (client) => client.kind !== IntegrationClientKind.Salesforce
  )?.kind;
  const integrationKind = selectedIntegrationKind ?? householdIntegrationLink;
  if (!integrationKind) return 'Integration';

  const provider = getProviderFromIntegrationClientKind(integrationKind);
  return getIntegrationProviderDisplayName(provider) || 'Integration';
}
